import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit } from '../services';

/** Initial state */
const initialState = {
  apiCallStatusTrack: {
    guide: false,
    tags: false,
    artefacts: false,
    artefact: false,
    artefactUnit: false
  },
  loading: false,
  data: null,
  error: null,
  guide: [],
  tags: [],
  artefacts: [],
  artefact: {},
  artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[ONBOARD]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
  [
    'ONBOARD_DATA_REQUEST',
    'ONBOARD_DATA_SUCCESS',
    'ONBOARD_DATA_ERROR',
    'ONBOARD_DATA_RESET',
    'LOOKUP_ONBOARD_GUIDE',
    'SET_ONBOARD_GUIDE',
    'SET_ONBOARD_GUIDE_ON',
    'LOOKUP_ONBOARD_TAGS',
    'SET_ONBOARD_TAGS',
    'LOOKUP_ONBOARD_ARTEFACTS',
    'SET_ONBOARD_ARTEFACTS',
    'LOOKUP_ONBOARD_ARTEFACT',
    'SET_ONBOARD_ARTEFACT',
    'LOOKUP_ONBOARD_ARTEFACT_UNIT',
    'SET_ONBOARD_ARTEFACT_UNIT',
    'RESET_ONBOARD'
  ],
  namespacedPrefix
);

const {
  ONBOARD_DATA_REQUEST,
  ONBOARD_DATA_SUCCESS,
  ONBOARD_DATA_ERROR,
  ONBOARD_DATA_RESET,
  LOOKUP_ONBOARD_GUIDE,
  SET_ONBOARD_GUIDE,
  SET_ONBOARD_GUIDE_ON,
  LOOKUP_ONBOARD_TAGS,
  SET_ONBOARD_TAGS,
  LOOKUP_ONBOARD_ARTEFACTS,
  SET_ONBOARD_ARTEFACTS,
  LOOKUP_ONBOARD_ARTEFACT,
  SET_ONBOARD_ARTEFACT,
  LOOKUP_ONBOARD_ARTEFACT_UNIT,
  SET_ONBOARD_ARTEFACT_UNIT,
  RESET_ONBOARD
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
  /** auth data request */
  [ONBOARD_DATA_REQUEST](state) {
    Object.assign(state, { loading: true, error: null });
  },

  /** auth data success */
  [ONBOARD_DATA_SUCCESS](state, payload) {
    Object.assign(state, { loading: false, data: payload, user: payload, isAuthenticated: true });
  },

  /** auth data error */
  [ONBOARD_DATA_ERROR](state, payload) {
    Object.assign(state, {
      loading: false,
      data: null,
      error: payload || true
    });
  },

  /** reset auth data */
  [ONBOARD_DATA_RESET](state) {
    Object.assign(state, initialState);
  },

  [LOOKUP_ONBOARD_GUIDE](state) {
    state.loading = true;
    state.apiCallStatusTrack.guide = true;
  },
  [SET_ONBOARD_GUIDE](state, payload) {
    state.guide = payload.res.data;
    state.loading = false;
    state.apiCallStatusTrack.guide = false;
  },
  [SET_ONBOARD_GUIDE_ON](state, guide) {
    state.guideOn = guide;
    state.loading = false;
  },
  [LOOKUP_ONBOARD_TAGS](state) {
    state.loading = true;
    state.apiCallStatusTrack.tags = true;
  },
  [SET_ONBOARD_TAGS](state, payload) {
    state.tags = payload.res.data;
    state.tagsCount = payload.res.data ? payload.res.data.length : 0;
    state.loading = false;
    state.apiCallStatusTrack.tags = false;
  },
  [LOOKUP_ONBOARD_ARTEFACTS](state) {
    state.loading = true;
    state.apiCallStatusTrack.artefacts = true;
  },
  [SET_ONBOARD_ARTEFACTS](state, payload) {
    state.artefacts = payload.res.data;
    state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
    state.loading = false;
    state.apiCallStatusTrack.artefacts = false;
  },
  [LOOKUP_ONBOARD_ARTEFACT](state) {
    state.loading = true;
    state.apiCallStatusTrack.artefact = true;
  },
  [SET_ONBOARD_ARTEFACT](state, payload) {
    //   state.artefact = payload;
    //   state.artefact = Object.assign({}, state.artefact, payload)
    state.artefact = {};
    state.artefact = payload.res.data;
    state.loading = false;
    state.apiCallStatusTrack.artefact = false;
  },
  [LOOKUP_ONBOARD_ARTEFACT_UNIT](state) {
    state.loading = true;
    state.apiCallStatusTrack.artefactUnit = true;
  },
  [SET_ONBOARD_ARTEFACT_UNIT](state, payload) {
    state.artefactUnit = payload;
    state.loading = false;
    state.apiCallStatusTrack.artefactUnit = false;
  },
  [RESET_ONBOARD](state) {
    for (let f in state) {
      Vue.set(state, f, state.initialState[f]);
    }
  }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
  'FETCH_ONBOARD_GUIDE',
  'FETCH_ONBOARD_TAGS',
  'FETCH_ONBOARD_ARTEFACTS',
  'FETCH_ONBOARD_ARTEFACT',
  'FETCH_ONBOARD_ARTEFACT_UNIT',
  'RESET_ONBOARD_STATE'
], namespacedPrefix);

/**
 * Onboard data actions
 */
const actions = {

  async [actionsTypes.FETCH_ONBOARD_GUIDE](context, payload) {
    context.commit(LOOKUP_ONBOARD_GUIDE);
    return await fetchGuide(payload)
      .then(data => {
        payload.res = data;
        context.commit(SET_ONBOARD_GUIDE, payload);
      })
      .catch(error => {
        payload.res = error;
        context.commit(SET_ONBOARD_GUIDE, payload);
      });
  },
  async [actionsTypes.FETCH_ONBOARD_TAGS]({ commit }, payload) {
    commit(LOOKUP_ONBOARD_TAGS);
    return await fetchTags(payload)
      .then(data => {
        payload.res = data;
        commit(SET_ONBOARD_TAGS, payload);
      })
      .catch(error => {
        payload.res = error;
        commit(SET_ONBOARD_TAGS, payload);
      });
  },
  async [actionsTypes.FETCH_ONBOARD_ARTEFACTS]({ commit }, payload) {
    commit(LOOKUP_ONBOARD_ARTEFACTS);
    return await fetchArtefacts(payload)
      .then(data => {
        payload.res = data;
        commit(SET_ONBOARD_ARTEFACTS, payload);
      })
      .catch(error => {
        payload.res = error;
        commit(SET_ONBOARD_ARTEFACTS, payload);
      });
  },
  async [actionsTypes.FETCH_ONBOARD_ARTEFACT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_ONBOARD_ARTEFACT);
    if (prevArtifact !== undefined) {
      return context.commit(SET_ONBOARD_ARTEFACT, prevArtifact);
    }
    return await fetchArtefact(payload)
      .then(data => {
        payload.res = data;
        context.commit(SET_ONBOARD_ARTEFACT, payload);
        return data;
      })
      .catch(error => {
        payload.res = { error: 'No records found for the search criteria.' };
        context.commit(SET_ONBOARD_ARTEFACT, payload);
        // throw new Error(error);
      });
  },
  async [actionsTypes.FETCH_ONBOARD_ARTEFACT_UNIT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_ONBOARD_ARTEFACT_UNIT);
    if (prevArtifact !== undefined) {
      return context.commit(SET_ONBOARD_ARTEFACT_UNIT, prevArtifact);
    }
    return await fetchArtefactUnit(payload)
      .then(data => {
        payload.res.order = data.data;
        context.commit(SET_ONBOARD_ARTEFACT_UNIT, payload);
      })
      .catch(error => {
        payload.res = { error: 'No records found for the given criteria.' };
        context.commit(SET_ONBOARD_ARTEFACT_UNIT, payload);
      });
  },
  [actionsTypes.RESET_ONBOARD_STATE]({ commit }) {
    commit(RESET_ONBOARD);
  }
};

/**
 * Users data actions
 */
const state = {
  ...initialState,
  guideOn: [],
  tagsCount: 0,
  artefactsCount: 0
};

const getters = {
  isLoading(state) {
    state.loading = false;
    for (let x in state.apiCallStatusTrack) {
      if(state.apiCallStatusTrack[x]) {
        state.loading = true;
        break;
      }
    }
    return state.loading;
  },
  tagsCount(state) {
    return state.tagsCount;
  },
  tags(state) {
    return state.tags;
  },
  artefactsCount(state) {
    return state.artefactsCount;
  },
  artefacts(state) {
    return state.artefacts;
  },
  artefact(state) {
    return state.artefact;
  },
  artefactUnit(state) {
    return state.artefactUnit;
  },
  guide(state) {
    return state.guide;
  },
  guideOn(state) {
    return state.guideOn;
  }
};

export default {
  mutations,
  actions,
  state,
  getters
};
